import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { changeAuthorGroup, deleteGroupUser, setSelectedUser } from '@/store/chat/actions';

import { selectChat } from '@/store/chat/selectors';
import { selectUsers } from '@/store/users/selectors';

import { Avatar, Checkbox, Popover, Tooltip } from 'antd';
import { getContrastColor } from '@/pages/Chat/utils';
import { FiMoreVertical } from 'react-icons/fi';

import useImage from '@shared/hooks/useImage';

import cn from 'classnames';
import PropTypes from 'prop-types';

import styles from '../Messages.module.scss';

const ChatUser = ({
  data,
  chatId,
  onSelect,
  canSelect = false,
  isSelected = false,
  canDelete = false,
  withOutGroup = false,
}) => {
  const avatar = useImage(data?.localPhotoUuid, 'avatar');
  const dispatch = useDispatch();

  const { t } = useTranslation('chat');
  const { selectedUser } = useSelector(selectChat);
  const { currentUser } = useSelector(selectUsers);

  const [visiblePopover, setVisiblePopover] = useState(false);

  const handleUpdateAuthor = () => {
    dispatch(
      changeAuthorGroup(chatId, data.id, () => {
        dispatch(setSelectedUser({ ...selectedUser, authorId: data.id }));
      })
    );
  };

  const handleDeleteUser = () => {
    dispatch(deleteGroupUser(chatId, data.id));
  };

  const contentPopover = (
    <>
      <div onClick={handleUpdateAuthor} className={styles.groupUser__element}>
        <p>{t('toOwner')}</p>
      </div>
      <div onClick={handleDeleteUser} className={cn(styles.groupUser__element, styles.groupUser__element_red)}>
        <p>{t('delete')}</p>
      </div>
    </>
  );

  return (
    <div className={cn(styles.groupUser, { [styles.groupUser_pointer]: canSelect })} onClick={onSelect}>
      <Link
        to={'/collegues/' + data.id}
        className={styles.groupUser__box}
        onClick={e => canSelect && e.preventDefault()}
      >
        <Avatar size={34} src={avatar} />
        <div className={styles.groupUser__main}>
          <div className={styles.groupUser__container}>
            <div className={styles.groupUser__tags}>
              {!!data?.groups && !withOutGroup && (
                <Tooltip title={data.groups.groupName}>
                  <p
                    style={{ backgroundColor: data.groups.groupColor, color: getContrastColor(data.groups.groupColor) }}
                    className={styles.groupUser__position}
                  >
                    {data.groups.groupName}
                  </p>
                </Tooltip>
              )}
              {!!data.position && (
                <Tooltip title={data.position?.name}>
                  <p className={styles.groupUser__position}>{data.position?.name}</p>
                </Tooltip>
              )}
            </div>
            <p className={styles.groupUser__name}>
              {data.firstName} {data.lastName}
            </p>
          </div>
        </div>
      </Link>
      {currentUser?.id === selectedUser?.authorId && selectedUser?.authorId !== data.id && !canSelect && canDelete && (
        <Popover trigger='click' open={visiblePopover} content={contentPopover} onOpenChange={setVisiblePopover}>
          <FiMoreVertical size={20} onClick={() => setVisiblePopover(true)} className={styles.groupUser__icon} />
        </Popover>
      )}
      {selectedUser?.authorId === data.id && !canSelect && <p className={styles.groupUser__owner}>{t('owner')}</p>}
      {canSelect && <Checkbox checked={isSelected} />}
    </div>
  );
};

ChatUser.propTypes = {
  chatId: PropTypes.number,
  canSelect: PropTypes.bool,
  canDelete: PropTypes.bool,
  isSelected: PropTypes.bool,
  withOutGroup: PropTypes.bool,
  onSelect: PropTypes.func,
  data: PropTypes.shape({
    id: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    localPhotoUuid: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }),
};

export default memo(ChatUser);
